import React, { useEffect, useMemo, useState, useRef, useCallback } from "react";
import { Box } from "@mui/material";
import SecurityIcon from "@mui/icons-material/Security";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import VpnKeyIcon from "@mui/icons-material/VpnKey";
import SearchIcon from "@mui/icons-material/Search";
import { NGROK } from "../../../../APIs";
import axios from "axios";
import TailwindSwitch from "../../../../tailwind-ui/components/TailwindSwitch";

const BLUE = "#3b83f6";

const Sk = ({ className = "" }) => (
  <div className={`animate-pulse rounded-md bg-gray-200 dark:bg-gray-700/60 ${className}`} />
);

const RolesListSkeleton = () => (
  <div className="flex flex-col w-full gap-4 font-sans">
    <div className="inline-flex rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 p-1 w-fit">
      <Sk className="h-9 w-16 rounded-lg mx-1" />
      <Sk className="h-9 w-20 rounded-lg mx-1" />
      <Sk className="h-9 w-20 rounded-lg mx-1" />
    </div>

    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
      <div className="bg-white dark:bg-gray-900 rounded-2xl border border-gray-200 dark:border-gray-700 overflow-hidden shadow-sm flex flex-col h-[calc(100vh-320px)] max-h-[740px]">
        <div className="text-white text-center py-3.5 font-semibold tracking-wide" style={{ backgroundColor: BLUE }}>
          Publish Roles
        </div>

        <div className="p-3 border-b border-gray-200 dark:border-gray-700">
          <Sk className="h-10 w-full rounded-xl" />
        </div>

        <div className="flex-1 overflow-y-auto">
          {Array.from({ length: 10 }).map((_, idx) => (
            <div
              key={idx}
              className="flex items-center justify-between px-4 py-3 border-b border-gray-100 dark:border-gray-800"
            >
              <div className="flex items-center gap-3 min-w-0 flex-1">
                <Sk className="h-9 w-9 rounded-full flex-shrink-0" />
                <div className="min-w-0 flex-1">
                  <Sk className="h-4 w-2/3 rounded" />
                  <Sk className="h-3 w-1/3 rounded mt-2" />
                </div>
              </div>
              <Sk className="h-6 w-12 rounded-full flex-shrink-0" />
            </div>
          ))}
        </div>
      </div>

      <div className="bg-white dark:bg-gray-900 rounded-2xl border border-gray-200 dark:border-gray-700 overflow-hidden shadow-sm flex flex-col h-[calc(100vh-320px)] max-h-[740px]">
        <div className="p-5 border-b border-gray-200 dark:border-gray-700">
          <div className="flex items-center gap-3">
            <Sk className="h-10 w-10 rounded-full" />
            <div className="min-w-0 flex-1">
              <Sk className="h-5 w-2/3 rounded" />
              <Sk className="h-4 w-1/3 rounded mt-2" />
            </div>
          </div>
        </div>

        <div className="px-5 pt-4">
          <div className="flex gap-6 border-b border-gray-200 dark:border-gray-700 pb-3">
            <Sk className="h-4 w-16 rounded" />
            <Sk className="h-4 w-24 rounded" />
          </div>
        </div>

        <div className="p-5 flex-1 overflow-auto space-y-5">
          <div>
            <Sk className="h-3 w-24 rounded" />
            <Sk className="h-4 w-2/3 rounded mt-2" />
          </div>
          <div className="border-t border-gray-100 dark:border-gray-800 pt-5">
            <Sk className="h-3 w-20 rounded" />
            <Sk className="h-4 w-full rounded mt-2" />
          </div>
          <div className="border-t border-gray-100 dark:border-gray-800 pt-5">
            <Sk className="h-3 w-24 rounded" />
            <Sk className="h-4 w-full rounded mt-2" />
            <Sk className="h-4 w-5/6 rounded mt-2" />
          </div>
        </div>
      </div>
    </div>
  </div>
);

const GCPGenericRoles = ({ latestTenant, projectId }) => {
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);
  const [roles, setRoles] = useState([]);
  const [filter, setFilter] = useState("all");
  const [selectedDetailsTab, setSelectedDetailsTab] = useState(0);
  const [pageNo, setPageNo] = useState(1);
  const [reachedEnd, setReachedEnd] = useState(false);
  const [searchString, setSearchString] = useState("");
  const [isrolesLoading, setIsrolesLoading] = useState(false);
  const [selectedRole, setSelectedRole] = useState(null);

  const isFetchingRef = useRef(false);
  const lastFetchParamsRef = useRef({ pageNo: 0, searchString: "" });

  const filterRoles = useCallback((allRoles, f) => {
    if (f === "default") return allRoles?.filter((role) => !role.isCustom);
    if (f === "custom") return allRoles?.filter((role) => role.isCustom);
    return allRoles;
  }, []);

  const filteredRoles = useMemo(() => {
    const base = (roles || []).filter((r) => r?.roleName);
    return filterRoles(base, filter);
  }, [roles, filter, filterRoles]);

  useEffect(() => {
    if (filteredRoles?.length > 0 && !selectedRole) {
      setSelectedRole(filteredRoles[0]);
    } else if (filteredRoles?.length > 0 && selectedRole) {
      const stillExists = filteredRoles.some(
        (r) => (r.roleId || r.id) === (selectedRole.roleId || selectedRole.id)
      );
      if (!stillExists) {
        setSelectedRole(filteredRoles[0]);
      }
    } else if (filteredRoles?.length === 0) {
      setSelectedRole(null);
    }
  }, [filteredRoles]);

  const handleFilterChange = (newFilter) => {
    setFilter(newFilter);
    setSelectedDetailsTab(0);
  };

  const handleSearchSubmit = () => {
    setRoles([]);
    setPageNo(1);
    setReachedEnd(false);
    setSelectedRole(null);
    setSelectedDetailsTab(0);
    lastFetchParamsRef.current = { pageNo: 0, searchString: "" };
    setSearchString(search.trim());
  };

  const handleRoleSearchChange = (event) => {
    const value = event.target.value;
    setSearch(value);

    if (!value || value.trim() === "") {
      setRoles([]);
      setPageNo(1);
      setReachedEnd(false);
      setSelectedRole(null);
      setSelectedDetailsTab(0);
      lastFetchParamsRef.current = { pageNo: 0, searchString: "" };
      setSearchString("");
    }
  };

  const handleKeyDown = (event) => {
    if (event.key === "Enter") {
      handleSearchSubmit();
    }
  };

  const handleRoleClick = (role) => {
    setSelectedRole(role);
    setSelectedDetailsTab(0);
  };

  const handlePublishToggle = async (event, roleId) => {
    const isManagedRole = event?.target?.checked;

    setRoles((prevData) =>
      prevData.map((role) =>
        role.roleId === roleId ? { ...role, isManagedRole } : role
      )
    );

    try {
      await axios.put(
        `${NGROK}/api/gcp/resource/publishRole?tenantName=${encodeURIComponent(
          latestTenant
        )}&projectId=${encodeURIComponent(projectId)}&roleId=${encodeURIComponent(
          roleId
        )}`
      );
    } catch (e) {
      setRoles((prevData) =>
        prevData.map((role) =>
          role.roleId === roleId ? { ...role, isManagedRole: !isManagedRole } : role
        )
      );
    }
  };

  const getAllRoles = async (page, searchStr) => {
    let url = `${NGROK}/api/gcp/resource/get-roles?tenantName=${latestTenant}&projectId=${projectId}&page=${page}&size=25`;
    if (searchStr && searchStr.length > 0) {
      url += `&searchString=${encodeURIComponent(searchStr)}`;
    }
    const response = await axios.get(url);
    if (response.status === 200) return response.data;
    return [];
  };

  const fetchData = async (page, searchStr, isInitialLoad = false) => {
    if (
      isFetchingRef.current &&
      lastFetchParamsRef.current.pageNo === page &&
      lastFetchParamsRef.current.searchString === searchStr
    ) {
      return;
    }

    if (
      !isInitialLoad &&
      page === lastFetchParamsRef.current.pageNo &&
      searchStr === lastFetchParamsRef.current.searchString
    ) {
      return;
    }

    isFetchingRef.current = true;
    lastFetchParamsRef.current = { pageNo: page, searchString: searchStr };

    try {
      const rolesData = await getAllRoles(page, searchStr);

      if (!rolesData || rolesData.length === 0) {
        setReachedEnd(true);
      } else {
        setReachedEnd(false);
      }

      if (page > 1) {
        setRoles((prevRoles) => [...prevRoles, ...rolesData]);
      } else {
        setRoles(rolesData);
      }

      setLoading(false);
      setIsrolesLoading(false);
    } catch (error) {
      console.error("Error fetching roles:", error);
      setLoading(false);
      setIsrolesLoading(false);
      setReachedEnd(true);
    } finally {
      isFetchingRef.current = false;
    }
  };

  useEffect(() => {
    if (projectId != null && latestTenant) {
      setLoading(true);
      setRoles([]);
      setPageNo(1);
      setReachedEnd(false);
      setSelectedRole(null);
      setSearch("");
      setSearchString("");
      lastFetchParamsRef.current = { pageNo: 0, searchString: "" };
      fetchData(1, "", true);
    }
  }, [latestTenant, projectId]);

  useEffect(() => {
    if (projectId != null && latestTenant) {
      if (lastFetchParamsRef.current.searchString !== searchString || searchString === "") {
        fetchData(1, searchString, false);
      }
    }
  }, [searchString]);

  useEffect(() => {
    if (pageNo > 1 && projectId != null && latestTenant && !reachedEnd) {
      fetchData(pageNo, searchString, false);
    }
  }, [pageNo]);

  const loadMoreRoles = useCallback(() => {
    if (reachedEnd || isrolesLoading || isFetchingRef.current) return;
    setIsrolesLoading(true);
    setPageNo((prevPage) => prevPage + 1);
  }, [reachedEnd, isrolesLoading]);

  const handleScroll = useCallback(
    (e) => {
      const { scrollTop, scrollHeight, clientHeight } = e.target;
      if (
        scrollHeight - scrollTop <= clientHeight + 100 &&
        !reachedEnd &&
        !isrolesLoading &&
        !isFetchingRef.current
      ) {
        loadMoreRoles();
      }
    },
    [loadMoreRoles, reachedEnd, isrolesLoading]
  );

  const selectedKey = selectedRole?.roleId || selectedRole?.id;

  return (
    <>
      {loading ? (
        <RolesListSkeleton />
      ) : (
        <div className="flex flex-col w-full gap-4 font-sans">
          <div className="inline-flex rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 p-1 w-fit">
            {[
              { key: "all", label: "All" },
              { key: "default", label: "Default" },
              { key: "custom", label: "Custom" },
            ].map((tab) => (
              <button
                key={tab.key}
                onClick={() => handleFilterChange(tab.key)}
                className={`px-5 py-2 text-sm font-medium rounded-lg transition-all duration-200 ${filter === tab.key
                  ? `text-white shadow-sm`
                  : "text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white hover:bg-gray-50 dark:hover:bg-gray-800"
                  }`}
                style={filter === tab.key ? { backgroundColor: BLUE } : undefined}
              >
                {tab.label}
              </button>
            ))}
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <div className="bg-white dark:bg-gray-900 rounded-2xl border border-gray-200 dark:border-gray-700 overflow-hidden shadow-sm flex flex-col h-[calc(100vh-320px)] max-h-[740px]">
              <div className="text-white text-center py-3.5 font-semibold tracking-wide" style={{ backgroundColor: BLUE }}>
                Publish Roles
              </div>

              <div className="p-3 border-b border-gray-200 dark:border-gray-700">
                <div className="relative">
                  <input
                    type="text"
                    placeholder="Search roles..."
                    value={search}
                    onChange={handleRoleSearchChange}
                    onKeyDown={handleKeyDown}
                    className="w-full pl-4 pr-10 py-2.5 text-sm border border-gray-200 dark:border-gray-600 rounded-xl bg-white dark:bg-gray-800 text-gray-900 dark:text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:border-transparent transition-all"
                    style={{ boxShadow: "none" }}
                  />
                  <button
                    onClick={handleSearchSubmit}
                    className="absolute right-2 top-1/2 -translate-y-1/2 p-1.5 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
                    aria-label="Search"
                    type="button"
                  >
                    <SearchIcon fontSize="small" />
                  </button>
                </div>
              </div>

              <div className="flex-1 overflow-y-auto" onScroll={handleScroll}>
                {filteredRoles?.length > 0 ? (
                  filteredRoles.map((role, index) => {
                    const roleKey = role.roleId || role.id || index;
                    const isSelected = roleKey === selectedKey;

                    return (
                      <div
                        key={roleKey}
                        onClick={() => handleRoleClick(role)}
                        className={`flex items-center justify-between px-4 py-3 border-b border-gray-100 dark:border-gray-800 cursor-pointer transition-all duration-150 ${isSelected
                          ? "border-l-4"
                          : "hover:bg-gray-50 dark:hover:bg-gray-800/50 border-l-4 border-l-transparent"
                          }`}
                        style={
                          isSelected
                            ? { backgroundColor: "rgba(59,131,246,0.06)", borderLeftColor: BLUE }
                            : undefined
                        }
                      >
                        <div className="flex items-center gap-3 min-w-0">
                          <div
                            className="w-9 h-9 rounded-full flex items-center justify-center flex-shrink-0"
                            style={{ backgroundColor: "rgba(59,131,246,0.12)" }}
                          >
                            <SecurityIcon sx={{ color: BLUE, fontSize: 20 }} />
                          </div>
                          <div className="min-w-0">
                            <p className="text-sm text-gray dark:text-white truncate">{role.roleName}</p>
                            <p className="text-xs text-gray-500 dark:text-gray-500 truncate">
                              {role.isCustom ? "Custom Role" : "Predefined Role"}
                            </p>
                          </div>
                        </div>

                        <div className="flex items-center flex-shrink-0" onClick={(e) => e.stopPropagation()}>
                          <TailwindSwitch
                            isDisabled={false}
                            action={(e, data) => handlePublishToggle(e, data.roleId)}
                            data={role}
                            isChecked={role.isManagedRole}
                          />
                        </div>
                      </div>
                    );
                  })
                ) : (
                  <div className="flex flex-col items-center justify-center py-16 text-gray-400">
                    <SecurityIcon sx={{ fontSize: 44, color: "rgba(0,0,0,0.25)" }} />
                    <p className="text-sm mt-2">No roles found</p>
                  </div>
                )}

                {isrolesLoading && (
                  <div className="flex justify-center py-4">
                    <div
                      className="w-6 h-6 border-2 border-t-transparent rounded-full animate-spin"
                      style={{ borderColor: BLUE, borderTopColor: "transparent" }}
                    />
                  </div>
                )}
              </div>
            </div>

            <div className="bg-white dark:bg-gray-900 rounded-2xl border border-gray-200 dark:border-gray-700 overflow-hidden shadow-sm flex flex-col h-[calc(100vh-320px)] max-h-[740px]">
              {selectedRole ? (
                <>
                  <div className="p-5 border-b border-gray-200 dark:border-gray-700">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-full flex items-center justify-center" style={{ backgroundColor: "rgba(59,131,246,0.12)" }}>
                        <SecurityIcon sx={{ color: BLUE, fontSize: 22 }} />
                      </div>
                      <div className="min-w-0">
                        <h3 className="text-lg font-semibold text-gray-500 dark:text-white truncate">{selectedRole?.roleName}</h3>
                        <p className="text-sm text-gray-500 dark:text-gray-500">
                          {selectedRole?.isCustom ? "Custom Role" : "Predefined Role"}
                        </p>
                      </div>
                    </div>
                  </div>

                  <div className="px-5 pt-4">
                    <div className="flex gap-6 border-b border-gray-200 dark:border-gray-700">
                      <button
                        onClick={() => setSelectedDetailsTab(0)}
                        className={`flex items-center gap-2 pb-3 text-sm font-medium border-b-2 transition-all ${selectedDetailsTab === 0
                          ? "border-b-2"
                          : "text-gray-500 border-transparent hover:text-gray-700 dark:hover:text-gray-300"
                          }`}
                        style={selectedDetailsTab === 0 ? { color: BLUE, borderColor: BLUE } : undefined}
                      >
                        <InfoOutlinedIcon fontSize="small" />
                        Info
                      </button>
                      <button
                        onClick={() => setSelectedDetailsTab(1)}
                        className={`flex items-center gap-2 pb-3 text-sm font-medium border-b-2 transition-all ${selectedDetailsTab === 1
                          ? "border-b-2"
                          : "text-gray-500 border-transparent hover:text-gray-700 dark:hover:text-gray-300"
                          }`}
                        style={selectedDetailsTab === 1 ? { color: BLUE, borderColor: BLUE } : undefined}
                      >
                        <VpnKeyIcon fontSize="small" />
                        Permissions
                      </button>
                    </div>
                  </div>

                  <div className="p-5 flex-1 overflow-auto">
                    {selectedDetailsTab === 0 && (
                      <div className="space-y-5">
                        <div>
                          <label className="block text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-1.5">
                            Role Name
                          </label>
                          <p className="text-sm text-gray-900 dark:text-white">{selectedRole?.roleName || "—"}</p>
                        </div>

                        <div className="border-t border-gray-100 dark:border-gray-800 pt-5">
                          <label className="block text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-1.5">
                            Role ID
                          </label>
                          <p className="text-sm text-gray-900 dark:text-white font-mono break-all">{selectedRole?.roleId || "—"}</p>
                        </div>

                        <div className="border-t border-gray-100 dark:border-gray-800 pt-5">
                          <label className="block text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-1.5">
                            Description
                          </label>
                          <p className="text-sm text-gray-900 dark:text-white leading-relaxed">{selectedRole?.description || "—"}</p>
                        </div>
                      </div>
                    )}

                    {selectedDetailsTab === 1 && (
                      <div>
                        <div className="flex items-center gap-2 mb-4">
                          <span className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                            Attached Permissions
                          </span>
                          <span
                            className="px-2 py-0.5 text-xs font-semibold rounded-full"
                            style={{ backgroundColor: "rgba(59,131,246,0.12)", color: BLUE }}
                          >
                            {selectedRole?.permissions?.length || 0}
                          </span>
                        </div>

                        <div className="space-y-2">
                          {selectedRole?.permissions?.length > 0 ? (
                            selectedRole.permissions.map((perm, idx) => (
                              <div key={idx} className="flex items-center gap-2 px-3 py-2 bg-gray-50 dark:bg-gray-800/50 rounded-xl">
                                <div className="w-1.5 h-1.5 rounded-full flex-shrink-0" style={{ backgroundColor: BLUE }} />
                                <span className="text-sm text-gray-700 dark:text-gray-300 font-mono break-all">{perm}</span>
                              </div>
                            ))
                          ) : (
                            <div className="flex flex-col items-center justify-center py-12 text-gray-400">
                              <VpnKeyIcon sx={{ fontSize: 36, color: "rgba(0,0,0,0.25)" }} />
                              <p className="text-sm mt-2">No permissions attached</p>
                            </div>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                </>
              ) : (
                <div className="flex flex-col items-center justify-center h-full text-gray-400">
                  <SecurityIcon sx={{ fontSize: 52, color: "rgba(0,0,0,0.25)" }} />
                  <p className="text-sm mt-2">Select a role to view details</p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default GCPGenericRoles;